import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Line-level actions for the Metabolae cart drawer.
 *
 * Two interactions live here, both ported from metabolae.com:
 *
 * - `[data-mcd-remove]` drops a line.
 * - `[data-mcd-tier]` swaps a line onto another bundle variant. Shopify cannot
 *   change a line's variant in place, so it is an add of the target tier
 *   followed by a remove of the old line, keeping the selling plan if one is
 *   attached.
 *
 * Rendering is left to the rest of the theme: each mutation resolves a
 * `CartLinesUpdateEvent`, which is what `cart-items-component` (morphs the
 * drawer section), `cart-icon` and `header-actions` (bubble count) already
 * listen for. Section HTML comes back with the mutation response, so no extra
 * round trip is needed.
 */

const DRAWER_SECTION_ID = 'cart-drawer-section';

/** Guards against overlapping mutations — the cart is a single shared resource. */
let mutating = false;

/** @returns {HTMLElement | null} */
function drawer() {
  return document.querySelector('#cart-drawer .mcd-shell');
}

/** @param {string} message */
function showError(message) {
  const target = drawer()?.querySelector('.mcd-err');
  if (!(target instanceof HTMLElement)) return;

  target.textContent = message;
  target.hidden = false;
}

/**
 * @param {string} key - The cart line key.
 * @param {boolean} state - Whether the line is mid-flight.
 */
function setBusy(key, state) {
  for (const row of document.querySelectorAll(`#cart-drawer .mcd-line[data-key="${CSS.escape(key)}"]`)) {
    if (state) row.setAttribute('data-busy', '1');
    else row.removeAttribute('data-busy');
  }
}

/** `Theme.routes.cart_add_url` already ends in `.js`; the update route does not. */
function cartUpdateUrl() {
  const url = Theme.routes.cart_update_url;
  return url.endsWith('.js') ? url : `${url}.js`;
}

/** Every cart-backed section on the page, so one response refreshes them all. */
function sectionsToUpdate() {
  const ids = new Set([DRAWER_SECTION_ID]);

  for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
  }

  return Array.from(ids).join(',');
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @param {boolean} [withSections] - Ask for section HTML back. Skip it for the
 *   intermediate step of a tier swap, whose markup is thrown away anyway.
 * @returns {Promise<any>}
 */
async function postCart(url, body, withSections = true) {
  const payload = withSections
    ? { ...body, sections: sectionsToUpdate(), sections_url: window.location.pathname }
    : body;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) throw new Error(data.description || data.message || 'Cart update failed');

  return data;
}

/**
 * Runs a cart mutation and announces it so the rest of the theme re-renders.
 *
 * @param {'remove' | 'update'} action
 * @param {Array<{ id: string, quantity: number }>} lines
 * @param {() => Promise<any>} mutate - Resolves with the final `/cart/*.js` payload.
 * @returns {Promise<void>}
 */
function announce(action, lines, mutate) {
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  return mutate()
    .then((cart) => {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'mcd-cart-drawer',
          didError: false,
        },
      });
    })
    .catch((error) => {
      deferred.reject(error);
      throw error;
    });
}

/**
 * @param {string} key
 * @param {HTMLButtonElement} button
 */
function removeLine(key, button) {
  if (!key || mutating) return;

  mutating = true;
  setBusy(key, true);
  button.disabled = true;

  announce('remove', [{ id: key, quantity: 0 }], () => postCart(cartUpdateUrl(), { updates: { [key]: 0 } }))
    .catch(() => {
      setBusy(key, false);
      button.disabled = false;
      showError("That didn't go through — please try again.");
    })
    .finally(() => {
      mutating = false;
    });
}

/**
 * Moves a line onto another bundle tier: add the target, then drop the original.
 *
 * @param {string} key - The line to replace.
 * @param {string} variantId - The tier to move to.
 * @param {string} sellingPlan - Selling plan to carry over, if any.
 * @param {HTMLButtonElement} button
 */
function setTier(key, variantId, sellingPlan, button) {
  if (!key || !variantId || mutating) return;

  mutating = true;
  setBusy(key, true);
  button.disabled = true;

  /** @type {Record<string, unknown>} */
  const addBody = { id: variantId, quantity: 1 };
  if (sellingPlan) addBody.selling_plan = sellingPlan;

  announce('update', [{ id: variantId, quantity: 1 }], async () => {
    await postCart(Theme.routes.cart_add_url, addBody, false);
    return postCart(cartUpdateUrl(), { updates: { [key]: 0 } });
  })
    .catch(() => {
      setBusy(key, false);
      button.disabled = false;
      showError("We couldn't change the bundle. Your cart is unchanged.");
    })
    .finally(() => {
      mutating = false;
    });
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const remove = target.closest('[data-mcd-remove]');
  if (remove instanceof HTMLButtonElement) {
    event.preventDefault();
    removeLine(remove.dataset.key ?? '', remove);
    return;
  }

  const tier = target.closest('[data-mcd-tier]');
  if (tier instanceof HTMLButtonElement && !tier.disabled) {
    event.preventDefault();
    setTier(tier.dataset.key ?? '', tier.dataset.to ?? '', tier.dataset.plan ?? '', tier);
  }
});
